import React, { useState, useMemo, useCallback } from 'react';
import { Block } from '../classes/Block';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import BlockCreationPopup from './BlockCreationPopup';
import './BlockTabs.css';
import { t } from '@lingui/core/macro';

interface BlockTabsProps {
  blocks: Block[];
  selectedBlockIndex: number | null;
  onBlockSelect: (index: number | null) => void;
  onBlocksChange: (newBlocks: Block[]) => void | Promise<void>;
}



const BlockTabs: React.FC<BlockTabsProps> = ({ 
  blocks, 
  selectedBlockIndex, 
  onBlockSelect, 
  onBlocksChange 
}) => {
  const [editingBlockIndex, setEditingBlockIndex] = useState<number | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const [hoveredBlockIndex, setHoveredBlockIndex] = useState<number | null>(null);
  const [animatingBlocks, setAnimatingBlocks] = useState<Set<number>>(new Set());
  const [showCreationPopup, setShowCreationPopup] = useState<boolean>(false);

  // Memoize filtered blocks to avoid recalculation on every render
  const filteredBlocks = useMemo(() => 
    blocks.filter(block => 
      block?.name?.trim() && block.name !== "undefined"
    ), [blocks]
  );

  // Memoize block index mapping for efficient lookups
  const blockIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    blocks.forEach((block, index) => {
      if (block?.name) {
        map.set(block.name, index);
      }
    });
    return map;
  }, [blocks]);

  const handleBlockClick = useCallback((blockName: string) => {
    const blockIndex = blockIndexMap.get(blockName);
    if (blockIndex !== undefined) {
      onBlockSelect(blockIndex);
    }
  }, [blockIndexMap, onBlockSelect]);

  const handleEditClick = useCallback((e: React.MouseEvent, blockIndex: number) => {
    e.stopPropagation();
    const block = blocks[blockIndex];
    if (block) {
      setEditingBlockIndex(blockIndex);
      setEditingName(block.name);
    }
  }, [blocks]);

  const handleDeleteClick = useCallback((e: React.MouseEvent, blockIndex: number) => {
    e.stopPropagation();
    const newBlocks = blocks.filter((_, index) => index !== blockIndex);
    const result = onBlocksChange(newBlocks);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onBlocksChange:', error);
      });
    }
    
    // Simplified selected index adjustment
    if (selectedBlockIndex !== null) {
      if (blockIndex === selectedBlockIndex) {
        onBlockSelect(newBlocks.length > 0 ? 0 : null);
      } else if (blockIndex < selectedBlockIndex) {
        onBlockSelect(selectedBlockIndex - 1);
      }
    }
  }, [blocks, selectedBlockIndex, onBlocksChange, onBlockSelect]);

  const handleRenameSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (editingBlockIndex !== null && editingName.trim()) {
      const newBlocks = [...blocks];
      newBlocks[editingBlockIndex] = {
        ...newBlocks[editingBlockIndex],
        name: editingName.trim()
      };
      const result = onBlocksChange(newBlocks);
      if (result instanceof Promise) {
        result.catch(error => {
          console.error('Error in onBlocksChange:', error);
        });
      }
      setEditingBlockIndex(null);
      setEditingName('');
    }
  }, [editingBlockIndex, editingName, blocks, onBlocksChange]);

  const handleRenameCancel = useCallback(() => {
    setEditingBlockIndex(null);
    setEditingName('');
  }, []);

  const handleAddBlock = useCallback(() => {
    setShowCreationPopup(true);
  }, []);

  const handleCreateEmptyBlock = useCallback(() => {
    const newBlock = new Block(
      `Block ${blocks.length + 1}`,
      true,                    // shouldDisplayBlockName
      'Arial',                 // blockNameFont
      30,                      // blockNameSize
      400,                     // blockNameWeight
      '#ffffff',               // blockNameColor
      1,                       // numColumns
      5,                       // labelValueGap
      45,                      // marginColumn
      0,                       // edgeDistance
      1,                       // distanceBetweenRows
      10,                      // distanceFromPreviousBlock
      false,                   // isSongBlock
      false,                   // isSingleCell
      false,                   // isCreditsList
      "•",                     // separator
      false,                   // isLogoBlock
      'Arial',                 // labelFontName
      11,                      // labelFontSize
      400,                     // labelFontWeight
      "right",                 // labelAlign
      "bottom",                 // labelVerticalAlign
      '#ffffff',               // labelFontColor
      'Arial',                 // valueFontName
      11,                      // valueFontSize
      400,                     // valueFontWeight
      "left",                  // valueAlign
      "bottom",                // valueVerticalAlign
      '#ffffff',               // valueFontColor
      false,                   // hasSeparateValueFormatting
      [{ col0: '', col1: 'New', col2: 'Block' }] // dataRows - one empty row with two columns
    );
    const newBlocks = [...blocks, newBlock];
    const result = onBlocksChange(newBlocks);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onBlocksChange:', error);
      });
    }
    const newBlockIndex = newBlocks.length - 1;
    onBlockSelect(newBlockIndex);
    
    // Immediately enter edit mode for the new block
    setEditingBlockIndex(newBlockIndex);
    setEditingName(newBlock.name);
  }, [blocks, onBlocksChange, onBlockSelect]);

  const handleCloneBlock = useCallback((sourceBlock: Block) => {
    // Create a deep copy of the source block
    const clonedBlock = new Block(
      `${sourceBlock.name} ` + t`(Copy)`,
      sourceBlock.shouldDisplayBlockName,
      sourceBlock.blockNameFont,
      sourceBlock.blockNameSize,
      sourceBlock.blockNameWeight,
      sourceBlock.blockNameColor,
      sourceBlock.numColumns,
      sourceBlock.labelValueGap,
      sourceBlock.marginColumn ?? 45,
      sourceBlock.edgeDistance,
      sourceBlock.distanceBetweenRows,
      sourceBlock.distanceFromPreviousBlock,
      sourceBlock.isSongBlock,
      sourceBlock.isSingleCell,
      sourceBlock.isCreditsList,
      sourceBlock.separator,
      sourceBlock.isLogoBlock,
      sourceBlock.labelFontName,
      sourceBlock.labelFontSize,
      sourceBlock.labelFontWeight,
      sourceBlock.labelAlign,
      sourceBlock.labelVerticalAlign,
      sourceBlock.labelFontColor,
      sourceBlock.valueFontName,
      sourceBlock.valueFontSize,
      sourceBlock.valueFontWeight,
      sourceBlock.valueAlign,
      sourceBlock.valueVerticalAlign,
      sourceBlock.valueFontColor,
      sourceBlock.hasSeparateValueFormatting,
      // Deep copy the data rows
      sourceBlock.dataRows.map(row => ({ ...row }))
    );
    
    const newBlocks = [...blocks, clonedBlock];
    const result = onBlocksChange(newBlocks);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onBlocksChange:', error);
      });
    }
    const newBlockIndex = newBlocks.length - 1;
    onBlockSelect(newBlockIndex);
    
    // Immediately enter edit mode for the new block
    setEditingBlockIndex(newBlockIndex);
    setEditingName(clonedBlock.name);
  }, [blocks, onBlocksChange, onBlockSelect]);

  // Unified move handler with animation
  const handleMoveBlock = useCallback((e: React.MouseEvent, blockIndex: number, direction: 'up' | 'down') => {
    e.stopPropagation();
    const newIndex = direction === 'up' ? blockIndex - 1 : blockIndex + 1;
    
    if (newIndex >= 0 && newIndex < blocks.length) {
      // Start animation
      setAnimatingBlocks(new Set([blockIndex, newIndex]));
      
      // Small delay to ensure CSS classes are applied
      setTimeout(() => {
        // Wait for animation to complete before updating state
        setTimeout(() => {
          const newBlocks = [...blocks];
          [newBlocks[blockIndex], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[blockIndex]];
          const result = onBlocksChange(newBlocks);
          if (result instanceof Promise) {
            result.catch(error => {
              console.error('Error in onBlocksChange:', error);
            });
          }
          
          // Adjust selected index
          if (selectedBlockIndex !== null) {
            if (selectedBlockIndex === blockIndex) {
              onBlockSelect(newIndex);
            } else if (selectedBlockIndex === newIndex) {
              onBlockSelect(blockIndex);
            }
          }
          
          // Clear animation state
          setAnimatingBlocks(new Set());
        }, 300); // Match the CSS transition duration
      }, 10); // Small delay to ensure CSS classes are applied
    }
  }, [blocks, selectedBlockIndex, onBlocksChange, onBlockSelect]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleRenameCancel();
    }
  }, [handleRenameCancel]);

  return (
    <div className="block-tabs-container">
      <div className="block-tabs">
        {filteredBlocks.map((block, index) => {
          const blockIndex = blockIndexMap.get(block.name) ?? -1;
          const isSelected = selectedBlockIndex === blockIndex;
          const isEditing = editingBlockIndex === blockIndex;
          const isHovered = hoveredBlockIndex === blockIndex;
          const isAnimating = animatingBlocks.has(blockIndex);
          
          // Determine animation class based on movement direction
          let animationClass = '';
          if (isAnimating) {
            // Find the other animating block to determine direction
            const animatingBlocksArray = Array.from(animatingBlocks);
            if (animatingBlocksArray.length === 2) {
              const otherBlockIndex = animatingBlocksArray.find(idx => idx !== blockIndex);
              if (otherBlockIndex !== undefined) {
                if (blockIndex < otherBlockIndex) {
                  animationClass = 'moving-down';
                } else {
                  animationClass = 'moving-up';
                }
              }
            }
          }

          return (
            <div 
              key={`${block.name}-${blockIndex}`}
              className={`block-tab ${isSelected ? 'active' : ''} ${animationClass}`}
              onClick={() => handleBlockClick(block.name)}
              onMouseEnter={() => setHoveredBlockIndex(blockIndex)}
              onMouseLeave={() => setHoveredBlockIndex(null)}
            >
              {isEditing ? (
                <form onSubmit={handleRenameSubmit} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onBlur={handleRenameCancel}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    className="block-rename-input"
                  />
                </form>
              ) : (
                <>
                  <span className="block-name">{block.name}</span>
                  {isHovered && (
                    <div className="block-actions">
                      <button
                        className="block-action-btn move-up-btn"
                        onClick={(e) => handleMoveBlock(e, blockIndex, 'up')}
                        title="Move block up"
                        disabled={blockIndex === 0}
                      >
                        <KeyboardArrowUpIcon fontSize="small" />
                      </button>
                      <button
                        className="block-action-btn move-down-btn"
                        onClick={(e) => handleMoveBlock(e, blockIndex, 'down')}
                        title="Move block down"
                        disabled={blockIndex === blocks.length - 1}
                      >
                        <KeyboardArrowDownIcon fontSize="small" />
                      </button>
                      <button
                        className="block-action-btn edit-btn"
                        onClick={(e) => handleEditClick(e, blockIndex)}
                        title="Rename block"
                      >
                        <EditIcon fontSize="small" />
                      </button>
                      <button
                        className="block-action-btn delete-btn"
                        onClick={(e) => handleDeleteClick(e, blockIndex)}
                        title="Delete block"
                      >
                        <DeleteIcon fontSize="small" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
        <button
          className="block-tab add-block-btn"
          onClick={handleAddBlock}
          title="Add new block"
        >
          <AddIcon fontSize="small" />
        </button>
      </div>
      
      <BlockCreationPopup
        isOpen={showCreationPopup}
        onClose={() => setShowCreationPopup(false)}
        onCreateEmpty={handleCreateEmptyBlock}
        onCloneBlock={handleCloneBlock}
        existingBlocks={filteredBlocks}
      />
    </div>
  );
};

export default BlockTabs; 